import React from 'react';
import { FaDownload, FaEye, FaSyncAlt, FaTrash } from 'react-icons/fa';
import { FinCheck } from '../FinField';
import { moeda } from '../../util/util';
import { dataBr, parcelasComNotas, situacaoNfParcela, somaMarcadas, statusConferenciaNf } from './nfParcelasPolicy';

// D-0099 — NF por parcela no Efetivo e no Financeiro.

export function NfParcelaPill({ parcela }) {
    if (!parcela) return null;
    const s = situacaoNfParcela(parcela);
    return <span className={'fin-pp__pill ' + s.tom}>{s.texto}</span>;
}

/**
 * Parcelas do plano com a NF de cada uma (ou "NF pendente"). Com `onAlternar`, cada parcela ganha a caixa de
 * marcar (pedido de NF, NF subida pela equipe, ajuste das parcelas de uma NF) e o rodapé soma as marcadas.
 */
export function NfParcelasTabela({ parcelas, marcadas = [], onAlternar = null, disabled = false, rotuloSoma = 'Soma das parcelas marcadas', idPrefixo = 'nfp' }) {
    const lista = parcelas || [];
    if (!lista.length) return null;
    const marcar = typeof onAlternar === 'function';
    // Três colunas (a data vai embaixo da parcela): cabe na coluna estreita do Efetivo sem quebrar valor nem selo.
    const semQuebra = { whiteSpace: 'nowrap' };
    return (
        <div className="fin-pp__tblwrap" data-nf-parcelas>
            <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                <thead>
                    <tr>
                        <th>Parcela</th>
                        <th className="text-end">Valor</th>
                        <th>Nota fiscal</th>
                    </tr>
                </thead>
                <tbody>
                    {lista.map((p) => (
                        <tr key={p.id}>
                            <td>
                                <div className="fin-pp__cell-strong" style={semQuebra}>
                                    {marcar ? (
                                        <FinCheck
                                            id={`${idPrefixo}-${p.id}`}
                                            label={`Parcela ${p.numero} de ${p.total}`}
                                            checked={marcadas.includes(String(p.id))}
                                            disabled={disabled}
                                            onChange={() => onAlternar(p.id)}
                                        />
                                    ) : `Parcela ${p.numero} de ${p.total}`}
                                </div>
                                <div className="fin-pp__cell-muted" style={{ fontSize: 12, ...semQuebra }}>
                                    {p.data_pagamento ? 'Pagamento ' + dataBr(p.data_pagamento) : 'Sem data de pagamento'}
                                </div>
                            </td>
                            <td className="fin-pp__cell-num" style={semQuebra}>R$ {moeda(p.valor)}</td>
                            <td style={semQuebra}><NfParcelaPill parcela={p} /></td>
                        </tr>
                    ))}
                </tbody>
                {marcar ? (
                    <tfoot>
                        <tr>
                            <td className="fin-pp__cell-muted">{rotuloSoma}</td>
                            <td className="fin-pp__cell-num fin-pp__cell-strong" style={semQuebra}>R$ {moeda(somaMarcadas(lista, marcadas))}</td>
                            <td />
                        </tr>
                    </tfoot>
                ) : null}
            </table>
        </div>
    );
}

/**
 * Efetivo › Documento Fiscal (Rafael 25/10): a NF de cada parcela com as ações da nota na própria linha —
 * NF (baixar), Status da conferência (com "analisar de novo" quando não está aprovada), Detalhes e Excluir.
 * Parcela com mais de uma NF ganha uma linha por nota; parcela e valor ocupam as linhas dela.
 */
export function NfParcelasNotasTabela({ parcelas, nfs, onBaixar, onDetalhes, onExcluir, onAnalisar, analisando = {}, excluindo = {} }) {
    const linhas = parcelasComNotas(parcelas, nfs);
    if (!linhas.length) return null;
    const semQuebra = { whiteSpace: 'nowrap' };
    const vazio = <span className="fin-pp__cell-muted">—</span>;
    const celulasDaParcela = (p, n) => (
        <>
            <td rowSpan={n}>
                <div className="fin-pp__cell-strong" style={semQuebra}>{`Parcela ${p.numero} de ${p.total}`}</div>
                <div className="fin-pp__cell-muted" style={{ fontSize: 12, ...semQuebra }}>
                    {p.data_pagamento ? 'Pagamento ' + dataBr(p.data_pagamento) : 'Sem data de pagamento'}
                </div>
            </td>
            <td rowSpan={n} className="fin-pp__cell-num" style={semQuebra}>R$ {moeda(p.valor)}</td>
        </>
    );
    return (
        <div className="fin-pp__tblwrap" data-nf-parcelas-notas>
            <table className="fin-pp__tbl fin-pp__tbl--meio" style={{ tableLayout: 'auto' }}>
                <thead>
                    <tr>
                        <th>Parcela</th>
                        <th className="text-end">Valor</th>
                        <th>Nota fiscal</th>
                        <th className="text-center">NF</th>
                        <th>Status</th>
                        <th className="text-center">Detalhes</th>
                        <th className="text-center">Excluir</th>
                    </tr>
                </thead>
                <tbody>
                    {linhas.map(({ parcela: p, notas }) => {
                        if (!notas.length) {
                            return (
                                <tr key={p.id}>
                                    {celulasDaParcela(p, 1)}
                                    <td style={semQuebra}><NfParcelaPill parcela={p} /></td>
                                    <td className="text-center">{vazio}</td>
                                    <td>{vazio}</td>
                                    <td className="text-center">{vazio}</td>
                                    <td className="text-center">{vazio}</td>
                                </tr>
                            );
                        }
                        return (
                            <React.Fragment key={p.id}>
                                {notas.map((nf, i) => {
                                    const st = statusConferenciaNf(nf);
                                    const nome = nf.numero ? 'NF nº ' + nf.numero : (nf.nome || 'Nota fiscal');
                                    return (
                                        <tr key={p.id + '-' + nf.id} data-nf-linha={nf.id}>
                                            {i === 0 ? celulasDaParcela(p, notas.length) : null}
                                            <td className="fin-pp__cell-strong" style={semQuebra}>{nome}</td>
                                            <td className="text-center">
                                                <button type="button" className="fin-pp__rowbtn is-ok" title={'Baixar ' + nome}
                                                    disabled={!nf.arquivo_id} onClick={() => onBaixar(nf)}>
                                                    <FaDownload size={13} />
                                                </button>
                                            </td>
                                            <td style={semQuebra}>
                                                <span className={'fin-pp__pill ' + st.tom}>{st.texto}</span>
                                                {st.analisarDeNovo && (
                                                    <button type="button" className="fin-pp__rowbtn fin-pp__rowbtn--sm" style={{ marginLeft: 6 }}
                                                        title="Analisar de novo: lê a nota e refaz a conferência"
                                                        disabled={!!analisando[nf.id]} onClick={() => onAnalisar(nf)}>
                                                        <FaSyncAlt size={11} className={analisando[nf.id] ? 'fin-pp__girando' : undefined} />
                                                    </button>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <button type="button" className="fin-pp__rowbtn" title={'Ver os detalhes da ' + nome}
                                                    disabled={!nf.id} onClick={() => onDetalhes(nf)}>
                                                    <FaEye size={14} />
                                                </button>
                                            </td>
                                            <td className="text-center">
                                                <button type="button" className="fin-pp__rowbtn is-danger"
                                                    title={nf.pode_remover === false ? 'A conta desta nota já foi paga ou está travada para pagamento' : 'Excluir a ' + nome}
                                                    disabled={!nf.id || nf.pode_remover === false || !!excluindo[nf.id]}
                                                    onClick={() => onExcluir(nf, p)}>
                                                    <FaTrash size={12} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
